import { CloudDownload, Print } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  FormControl,
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import exportAsPDF from 'services/exportAsPDF';
import { TimelineToPrint, TimelineTypeEnum, TimelineTypeFilterEnum } from 'types/timeline.types';
import { eventsAtom } from './eventsPage.atoms';
import useActionsParams from './useActionsParams';
import useEventsColumns from './useEventsColumns';
import { accountSelectedSelector } from 'atoms';
import DateRangePicker, { Value, ValuePiece } from 'components/DateRangePicker';

function EventsActions() {
  const { t } = useTranslation(['_common', 'events']);
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const events = useRecoilValue(eventsAtom);
  const columns = useEventsColumns({ t });
  const { beginDate, endDate, eventType, setEventType, setDates } = useActionsParams();

  const eventsToPrint = React.useMemo<TimelineToPrint[] | undefined>(
    () =>
      events
        ?.filter((event) => event.type !== TimelineTypeEnum.LOG)
        ?.map((event) => ({
          ...event,
          ...event.event,
          event: undefined,
          serviceOrder: undefined,
          images: undefined,
          time: event.date,
          observation:
            // @ts-ignore
            event?.event?.observation ??
            (!accountSelected?.hasPermissionToViewOccurrenceLogs
              ? ''
              : event?.logs
                  ?.map((log) => `${dayjs(log.date).format('L LTS')} - ${log.text}`)
                  ?.join('\n')),
        })),
    [events, accountSelected],
  );

  function handlePrintClick() {
    eventsToPrint &&
      exportAsPDF({
        rows: eventsToPrint,
        t,
        title:
          t('events:events_report') +
          (accountSelected
            ? ` - ${accountSelected?.displayName} (${accountSelected?.accountCode})`
            : ''),
        columns,
      });
  }

  const eventsCsvData = React.useMemo(() => {
    return (
      eventsToPrint && [
        columns.map((column) => {
          return column.description;
        }),
        ...eventsToPrint.map((event) =>
          columns.map((column) => {
            const value = event[column.dataKey] || '';
            return column.renderValue && value ? column.renderValue(value, event) : String(value);
          }),
        ),
      ]
    );
  }, [columns, eventsToPrint]);
  const dateRangeValue: [ValuePiece, ValuePiece] = React.useMemo(
    () => [new Date(beginDate), new Date(endDate)],
    [beginDate, endDate],
  );

  return (
    <Box
      sx={{
        flex: '0 0 auto',
      }}
    >
      <DateRangePicker
        onChange={(value: Value) => {
          if (Array.isArray(value)) {
            setDates(
              dayjs(value[0]).startOf('day').toISOString(),
              dayjs(value[1]).endOf('day').toISOString(),
            );
          }
        }}
        value={dateRangeValue}
        minDate={dayjs().subtract(3, 'months').toDate()}
        maxDate={dayjs().endOf('month').toDate()}
        label={t('_common:period')}
        minDetail="year"
      />

      <FormControl>
        <InputLabel id="event-type-filter-label">{t('events:type')}</InputLabel>
        <Select
          id="event-type-filter"
          labelId="event-type-filter-label"
          value={eventType}
          label={t('events:type')}
          onChange={(event: SelectChangeEvent) => setEventType(event.target.value as string)}
        >
          <MenuItem value={TimelineTypeFilterEnum.ALL}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.ALL}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.ALARM}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.ALARM}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.AUTOMATIC_TEST}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.AUTOMATIC_TEST}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.ARMED}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.ARMED}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.DISARMED}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.DISARMED}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.ARMED_DISARMED}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.ARMED_DISARMED}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.WITHOUT_CONTROL}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.WITHOUT_CONTROL}`)}
          </MenuItem>
          <MenuItem value={TimelineTypeFilterEnum.PATROL}>
            {t(`events:type_filter.${TimelineTypeFilterEnum.PATROL}`)}
          </MenuItem>
        </Select>
      </FormControl>

      <ButtonGroup variant="outlined" color="primary" sx={{ float: 'right' }}>
        <Button onClick={handlePrintClick} sx={{ borderColor: 'action.disabled' }}>
          <Print />
        </Button>
        <Button sx={{ borderColor: 'action.disabled' }}>
          <Box
            component={CSVLink}
            data={eventsCsvData || []}
            filename={`${
              t('events:events_report') +
              (accountSelected
                ? ` - ${accountSelected?.displayName} (${accountSelected?.accountCode})`
                : '')
            } - ${dayjs().format('L LTS')}`}
            sx={{
              color: 'currentcolor',
              textDecoration: 'none',
              display: 'flex',
            }}
          >
            <CloudDownload />
          </Box>
        </Button>
      </ButtonGroup>
    </Box>
  );
}

export default EventsActions;
